import { components } from './backend-types';

// ============================================================================
// Variety Mode
// ============================================================================

export const VARIETY_MODES = ['pantry_first', 'balanced', 'explore'] as const;
export type VarietyMode = components['schemas']['VarietyMode'];

export type PhysicalDeduction = components['schemas']['PhysicalDeduction'];

export function isValidVarietyMode(mode: unknown): mode is VarietyMode {
    return VARIETY_MODES.includes(mode as VarietyMode);
}

// ============================================================================
// Diet & Macro Axes (V8)
// ============================================================================

export type DietPhilosophy = components['schemas']['DietPhilosophy'];
export type MacroProfile = components['schemas']['MacroProfile'];
export type DietVerification = components['schemas']['DietVerification'];

// ============================================================================
// User Profile & Settings
// ============================================================================

export type UserTier = 'free' | 'pro' | 'premium' | 'unlimited';

export type Toggles = components['schemas']['Toggles'];
export type MealSettings = components['schemas']['MealSettings'];
export type WineSettings = components['schemas']['WineSettings'];

export type UserProfile = components['schemas']['ProfileResponse'];

// ============================================================================
// API Response Types
// ============================================================================

export type ProfileApiResponse = components['schemas']['ProfileResponse'];

export type UpdateTogglesPayload = components['schemas']['ToggleUpdate'];

// ============================================================================
// UI State Types
// ============================================================================

export type ViewType = 'current' | 'history' | 'favorites' | 'wine_cellar';

export interface UIState {
    view: ViewType;
    isDrawerOpen: boolean;
    isDarkMode: boolean;
    showToast: string | null;
    fadingId: string | null;
    isUpgradeModalOpen: boolean;
    upgradeFeature: string;
    actionLoading: string | null;
    fadingInventoryItem: string | null;
}

// ============================================================================
// Macro & Nutrition Types
// ============================================================================

export type Macros = components['schemas']['MacroUpdate'];

export interface MacroPreset {
    name: string;
    protein: number;
    carbs: number;
    fat: number;
}

// ============================================================================
// Inventory Types
// ============================================================================

export type InventoryItem = components['schemas']['InventoryItem'] & { remove?: boolean };

export type InventoryUpdate = {
    items?: InventoryItem[];
};

export interface InventoryState {
    inventory: InventoryItem[];
    inventoryDraft: InventoryItem[];
    newItem: InventoryItem;
    isAdding: boolean;
    updatedItems: string[];
    isLoading: boolean;
}
// ============================================================================
// Wine Cellar Types
// ============================================================================

export type WineItem = components['schemas']['WineItem'];

export interface WineState {
    wineCellar: WineItem[];
    wineDraft: WineItem[];
    editingWine: WineItem | null;
    isSavingWine: boolean;
    isWineScanning: boolean;
    wineUpdateLoading: number | null;
    newWine: string;
    isAddingWine: boolean;
}
// ============================================================================
// Meal Plan Types
// ============================================================================

export type Meal = components['schemas']['Meal'] & { id?: string | number | null };

export interface MealIdentifier {
    day: number | string;
    meal_type: string;
}

export type ShoppingListItem = components['schemas']['ShoppingListItem'];

export type MealPlanDetail = components['schemas']['MealPlanDetail'];
export type MealPlanSummary = Omit<MealPlanDetail, 'shopping_list'> & { shopping_list?: ShoppingListItem[] };

export type SavedMealPlanDetail = components['schemas']['SavedMealPlanDetail'];
export interface SavedMealPlanSummary extends Omit<SavedMealPlanDetail, 'plan_data'> {
    plan_data: MealPlanSummary;
}

// Legacy aliases
export type MealPlan = MealPlanDetail;
export type SavedMealPlan = SavedMealPlanDetail;

// Two-Tiered DTO Architecture for Favorites
export interface FavoriteRecipeSummary {
    id?: string | number | null;
    name: string;
    macros: Macros; // Using existing Macros type alias
    instructions: string | null; // Nullable for list optimization
}

export interface FavoriteRecipeDetail extends FavoriteRecipeSummary {
    instructions: string; // Strict string for detail view
}

export type FavoriteRecipe = FavoriteRecipeSummary | FavoriteRecipeDetail;

export interface MealPlanState {
    currentPlan: MealPlan;
    inventory: InventoryItem[];
    favorites: FavoriteRecipeSummary[];
    wine_cellar: WineItem[];
    recent_plans: SavedMealPlanSummary[];
    selectedMeal: MealIdentifier | null;
    chefMessage: string;
    isLoading: boolean;
    detailLoadingRegistry: Record<string | number, boolean>;
    abortController: AbortController | null;
    dirtyFields: Set<string>;
}

// Override generated response to enforce Summary type in the list
export type AppContextResponse = Omit<components['schemas']['AppContextResponse'], 'favorites' | 'recent_plans'> & {
    favorites: FavoriteRecipeSummary[];
    recent_plans: SavedMealPlanSummary[];
};
